import { ReactNode, useMemo } from 'react';
import {
  Checkbox,
  Skeleton,
  SkeletonItem,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
  makeStyles,
  mergeClasses,
} from '@fluentui/react-components';

import { NUMERIC, STROKE, SURFACE, TEXT } from '../theme/tokens';

export interface DataTableColumn<T> {
  key: string;
  header: ReactNode;
  render: (item: T) => ReactNode;
  numeric?: boolean;
  width?: string;
  align?: 'left' | 'right' | 'center';
}

// Generic selection model. When provided, DataTable prepends a 48px checkbox
// column and manages a header tri-state based on the intersection of the
// current-page row keys with `selectedKeys`. When absent, DataTable renders
// exactly as before — zero surface change for consumers (e.g. FD-048
// MigrationsListPage) that don't opt in.
export interface DataTableSelection<T> {
  selectedKeys: Set<string>;
  onSelectionChange: (next: Set<string>) => void;
  keyForSelection: (row: T) => string;
}

export interface DataTableProps<T> {
  columns: DataTableColumn<T>[];
  data: T[];
  keyForRow: (item: T) => string;
  onRowClick?: (item: T) => void;
  isLoading?: boolean;
  error?: ReactNode;
  emptyState?: ReactNode;
  loadingRowCount?: number;
  selection?: DataTableSelection<T>;
  // Optional DOM id per row — lets consumers deep-link/scroll to a row
  // (FD-098: device-grid mapping chips anchor into the mapping section).
  rowDomId?: (item: T) => string | undefined;
  /**
   * FD-275 (FD-ADR-031 §2) — the dark header band, for a table nested under a
   * heading that owns it: a group's campaigns, a campaign's devices.
   *
   * This is not decoration. An expanded micro-table with a light header reads as
   * an unrelated table that happens to be positioned below the heading; the band
   * is what makes it read as that heading's CONTENTS. Owner call, 2026-08-11.
   *
   * Deliberately NOT the default: a top-level list has nothing above it to
   * connect to, so a dark band there is just weight.
   */
  headerBand?: boolean;
  /**
   * FD-280 — `fixed` makes column widths authoritative rather than hints.
   *
   * Needed when SEVERAL DataTables must align with each other: the nested
   * campaign micro-tables under each expanded group are separate tables, and
   * under `auto` each one sizes to its own content, so the same column lands at
   * a different x-offset in every group. That reads as a rendering bug even
   * though each table is individually correct.
   *
   * Default stays `auto` — a single table should size to its content.
   */
  tableLayout?: 'auto' | 'fixed';
}

const CHECKBOX_COLUMN_WIDTH = '48px';

const useStyles = makeStyles({
  table: {
    width: '100%',
    tableLayout: 'auto',
  },
  tableFixed: {
    tableLayout: 'fixed',
  },
  // FD-467 — the AirBridge table look, folded in here rather than left as the
  // markup the preview repeated on eleven pages.
  //
  // STYLING ONLY. No prop was added and no prop changed meaning, which is what
  // lets `DataTable.test.tsx` stay untouched: the component has ~20 consumers
  // and an API change here is a twenty-file change wearing a one-file diff.
  //
  // The header is a quiet uppercase rule rather than Fluent's filled band,
  // because on a page that is already a stack of bordered SectionPanels a
  // second filled surface reads as a nested card. `headerBand` still exists for
  // the one case that genuinely needs weight — a micro-table hanging off a
  // heading that owns it — and is unchanged.
  headerCell: {
    fontSize: '11px',
    fontWeight: '600',
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    color: TEXT.secondary,
    borderBottomColor: STROKE.base,
  },
  bodyCell: {
    fontSize: '13px',
    borderBottomColor: STROKE.subtle,
  },
  // FD-ADR-043 D2 — no monospace family. This cell used to switch typeface to
  // align figures; `tabular-nums` does that job in Inter, which is why the mono
  // webfont could be retired rather than replaced.
  numericCell: {
    ...NUMERIC,
  },
  alignRight: {
    textAlign: 'right',
  },
  alignCenter: {
    textAlign: 'center',
  },
  clickableRow: {
    cursor: 'pointer',
    ':hover': {
      backgroundColor: SURFACE.rowFill,
    },
  },
  stateCell: {
    paddingTop: '32px',
    paddingBottom: '32px',
    textAlign: 'center',
  },
  skeletonCell: {
    paddingTop: '10px',
    paddingBottom: '10px',
  },
  selectionCell: {
    // The checkbox's built-in padding already provides enough click-target
    // space; trim the cell padding so the column stays at its 48px width.
    paddingLeft: '12px',
    paddingRight: '12px',
  },
  // The band is applied to the header CELLS rather than to the TableRow:
  // Fluent's TableHeaderCell paints its own background, so a colour on the row
  // is covered by every cell sitting on top of it. Setting it on the row looks
  // right in isolation and does nothing here.
  bandCell: {
    backgroundColor: SURFACE.tableBand,
    color: SURFACE.tableBandText,
    ':hover': {
      backgroundColor: SURFACE.tableBand,
    },
  },
});

export function DataTable<T>({
  columns,
  data,
  keyForRow,
  onRowClick,
  isLoading,
  error,
  emptyState,
  loadingRowCount = 8,
  selection,
  rowDomId,
  headerBand,
  tableLayout = 'auto',
}: DataTableProps<T>) {
  const styles = useStyles();

  const alignClass = (align?: 'left' | 'right' | 'center') =>
    align === 'right' ? styles.alignRight : align === 'center' ? styles.alignCenter : undefined;

  // Use mergeClasses, not string join — Griffel's atomic classes must be merged by the
  // library so competing properties (e.g. text-align) deduplicate correctly.
  const cellClass = (col: DataTableColumn<T>) =>
    mergeClasses(
      styles.bodyCell,
      col.numeric ? styles.numericCell : undefined,
      alignClass(col.align),
    );

  // Tri-state for the header checkbox — computed against current-page data
  // only (pagination-aware by design: header toggles affect what the user
  // can see). `allSelected` requires at least one visible row so the header
  // on an empty page stays unchecked, not "mixed".
  const currentKeys = useMemo(
    () => (selection ? data.map(selection.keyForSelection) : []),
    [data, selection],
  );
  const selectedOnPage = useMemo(() => {
    if (!selection) return 0;
    let count = 0;
    for (const key of currentKeys) {
      if (selection.selectedKeys.has(key)) count += 1;
    }
    return count;
  }, [currentKeys, selection]);

  const headerCheckedValue: boolean | 'mixed' =
    !selection || currentKeys.length === 0 || selectedOnPage === 0
      ? false
      : selectedOnPage === currentKeys.length
        ? true
        : 'mixed';

  const handleHeaderToggle = () => {
    if (!selection) return;
    const next = new Set(selection.selectedKeys);
    // If any row on this page is already selected, the header acts as
    // "clear current page". Otherwise it selects everything on the page.
    // This matches Excel / Outlook / most data grids and is intuitive for
    // a "tri-state" header.
    if (selectedOnPage > 0) {
      for (const key of currentKeys) next.delete(key);
    } else {
      for (const key of currentKeys) next.add(key);
    }
    selection.onSelectionChange(next);
  };

  const handleRowToggle = (row: T) => {
    if (!selection) return;
    const key = selection.keyForSelection(row);
    const next = new Set(selection.selectedKeys);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    selection.onSelectionChange(next);
  };

  // Number of body columns the state-row colspan must fill. When a checkbox
  // column is rendered, add 1 so error/empty rows still span the full table.
  const bodyColSpan = columns.length + (selection ? 1 : 0);

  return (
    <Table
      size="small"
      className={mergeClasses(
        styles.table,
        tableLayout === 'fixed' ? styles.tableFixed : undefined,
      )}
    >
      <TableHeader>
        <TableRow>
          {selection && (
            <TableHeaderCell
              style={{ width: CHECKBOX_COLUMN_WIDTH }}
              className={mergeClasses(
                styles.headerCell,
                styles.selectionCell,
                headerBand ? styles.bandCell : undefined,
              )}
            >
              <Checkbox
                checked={headerCheckedValue}
                onChange={handleHeaderToggle}
                aria-label={
                  headerCheckedValue === true
                    ? 'Clear selection'
                    : headerCheckedValue === 'mixed'
                      ? 'Clear selection'
                      : 'Select all rows on this page'
                }
              />
            </TableHeaderCell>
          )}
          {columns.map((col) => (
            <TableHeaderCell
              key={col.key}
              style={col.width ? { width: col.width } : undefined}
              className={mergeClasses(
                styles.headerCell,
                alignClass(col.align),
                headerBand ? styles.bandCell : undefined,
              )}
            >
              {col.header}
            </TableHeaderCell>
          ))}
        </TableRow>
      </TableHeader>
      <TableBody>
        {error ? (
          <TableRow>
            <TableCell colSpan={bodyColSpan} className={styles.stateCell}>
              {error}
            </TableCell>
          </TableRow>
        ) : isLoading ? (
          Array.from({ length: loadingRowCount }).map((_, rowIndex) => (
            <TableRow key={`loading-${rowIndex}`}>
              {selection && (
                <TableCell className={mergeClasses(styles.selectionCell, styles.skeletonCell)}>
                  <Skeleton>
                    <SkeletonItem size={16} style={{ width: '16px' }} />
                  </Skeleton>
                </TableCell>
              )}
              {columns.map((col) => (
                <TableCell key={col.key} className={styles.skeletonCell}>
                  <Skeleton>
                    <SkeletonItem size={16} />
                  </Skeleton>
                </TableCell>
              ))}
            </TableRow>
          ))
        ) : data.length === 0 ? (
          <TableRow>
            <TableCell colSpan={bodyColSpan} className={styles.stateCell}>
              {emptyState}
            </TableCell>
          </TableRow>
        ) : (
          data.map((item) => {
            const rowKey = keyForRow(item);
            const isSelected = selection
              ? selection.selectedKeys.has(selection.keyForSelection(item))
              : false;
            return (
              <TableRow
                key={rowKey}
                id={rowDomId ? rowDomId(item) : undefined}
                onClick={onRowClick ? () => onRowClick(item) : undefined}
                className={onRowClick ? styles.clickableRow : undefined}
              >
                {selection && (
                  <TableCell
                    className={styles.selectionCell}
                    // stopPropagation keeps row-level onClick (navigation)
                    // from firing when the user clicks inside the checkbox
                    // cell. Selection is orthogonal to navigation.
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Checkbox
                      checked={isSelected}
                      onChange={() => handleRowToggle(item)}
                      aria-label={isSelected ? `Deselect ${rowKey}` : `Select ${rowKey}`}
                    />
                  </TableCell>
                )}
                {columns.map((col) => (
                  <TableCell key={col.key} className={cellClass(col)}>
                    {col.render(item)}
                  </TableCell>
                ))}
              </TableRow>
            );
          })
        )}
      </TableBody>
    </Table>
  );
}
